import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import SynovusReview from '@/components/operations/hub/SynovusReview';
import SynovusClientReview from '@/components/operations/hub/SynovusClientReview';
import AutoTranslate from '@/components/operations/hub/AutoTranslate';
import { ArrowLeft, Plus, Trash2, Camera, Image as ImageIcon, ChevronDown, ChevronRight, FileText, Lock, ShieldAlert, CheckCircle2, Languages, Save, AlertTriangle, Users, Eye, Download } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const lbl = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500';
const inp = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]';
const SCALE = [[1, 'Poor'], [2, 'Needs Improvement'], [3, 'Acceptable'], [4, 'Very Good'], [5, 'Excellent']];
const SEV = ['Critical', 'High', 'Medium', 'Low'];
const etTime = () => new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' }) + ' ET';

// US phone: display 10 digits as (###) ###-####. Validation allows blank (optional).
const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').replace(/^1/, '');
  if (d.length !== 10) return v || '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const phoneValid = (v) => { if (!v || !v.trim()) return true; const d = v.replace(/\D/g, '').replace(/^1/, ''); return d.length === 10; };
const emailValid = (v) => { if (!v || !v.trim()) return true; return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()); };

// Two explicit tablet choices for every photo control: Take Photo (device camera
// via capture="environment") and Choose Existing Photo (gallery/files). A cancelled
// camera action fires no change event, so it never creates an empty attachment.
const PhotoControls = ({ onPick, testid }) => {
  const camRef = useRef(null); const galRef = useRef(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => camRef.current.click()} data-testid={`${testid}-camera`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#0E2A47] bg-[#0E2A47] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0E2A47]/90">
        <Camera size={13} /> Take Photo
      </button>
      <button type="button" onClick={() => galRef.current.click()} data-testid={`${testid}-gallery`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-[#0E2A47] hover:bg-slate-50">
        <ImageIcon size={13} /> Choose Existing Photo
      </button>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testid}-camera-input`}
        onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ''; }} />
      <input ref={galRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-gallery-input`}
        onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
};

// Thumbnail strip with a remove control for attached photos (section / before).
const PhotoThumbs = ({ photos, iid, token, onRemove }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {photos.map((p) => (
        <div key={p.id} className="relative w-20" data-testid={`syn-thumb-${p.id}`}>
          <img src={`${API}/operations/synovus/inspections/${iid}/photo/${p.id}/raw?auth=${token}`} alt={p.caption || 'photo'}
            onClick={() => window.open(`${API}/operations/synovus/inspections/${iid}/photo/${p.id}/raw?auth=${token}`, '_blank')}
            className="h-16 w-20 cursor-pointer rounded border border-slate-200 object-cover" />
          <button type="button" data-testid={`syn-thumb-remove-${p.id}`} onClick={() => onRemove(p.id)}
            className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white" title="Remove"><Trash2 size={11} /></button>
        </div>
      ))}
    </div>
  );
};

const SaveDraftBar = ({ onSave, saving, savedAt, extra }) => (
  <div className="flex flex-wrap items-center gap-2">
    <button onClick={() => onSave(false)} disabled={saving} data-testid="syn-save" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 disabled:opacity-50"><Save size={15} /> {saving ? 'Saving…' : 'Save Draft'}</button>
    {extra}
    {savedAt && <span className="text-xs text-[#4d7c0f]" data-testid="syn-saved-at">Saved · {savedAt}</span>}
  </div>
);

const SynovusInspection = ({ onBack, presetLocationId, presetInspectionId, presetDemo, embedded }) => {
  const [formLang, setFormLang] = useState('en');
  const { token, user } = useContext(AuthContext);
  const canConfidential = ['owner', 'admin', 'manager'].includes(user?.role);
  const isSergio = (user?.email || '').toLowerCase() === 'sperez@tfsi-us.com';
  const [config, setConfig] = useState(null);
  const [branches, setBranches] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [locId, setLocId] = useState(presetLocationId || '');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
  const [demo, setDemo] = useState(presetDemo ?? true);
  const [doc, setDoc] = useState(null);
  const [sections, setSections] = useState([]);
  const [rep, setRep] = useState({ present: null, name: '', title: '', phone: '', email: '', declined: false, acknowledged: false, signature_key: null });
  const [addl, setAddl] = useState([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [mgmtAssessment, setMgmtAssessment] = useState('');
  const [hasMgmt, setHasMgmt] = useState(false);
  const [open, setOpen] = useState({});
  const [result, setResult] = useState(null);
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showTrans, setShowTrans] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showClientReview, setShowClientReview] = useState(false);
  const [cleaners, setCleaners] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/customers`, auth(token)).then((r) => {
      const syn = (r.data || []).find((c) => /synovus/i.test(c.name));
      if (syn) axios.get(`${API}/service-locations/by-customer/${syn.id}`, auth(token)).then((x) => setBranches(x.data || []));
    });
    axios.get(`${API}/personnel`, auth(token)).then((r) => setPersonnel((r.data || []).filter((p) => p.is_active))).catch(() => {});
  }, [token]);

  const loadConfig = async (lid, savedAddl) => {
    const r = await axios.get(`${API}/operations/synovus/config?service_location_id=${lid}`, auth(token));
    setConfig(r.data);
    axios.get(`${API}/operations/synovus/assigned-cleaners?service_location_id=${lid}`, auth(token))
      .then((x) => setCleaners(x.data)).catch(() => setCleaners(null));
    setAddl(r.data.additional_services.map((a) => {
      const s = (savedAddl || []).find((x) => x.key === a.key);
      return { key: a.key, label: a.label, observed: s?.observed || false, location: s?.location || '', note: s?.note || '' };
    }));
    return r.data;
  };

  const hydrate = (d) => {
    setDoc(d); setSections(d.sections || []);
    setRep({ present: null, name: '', title: '', phone: '', email: '', declined: false, acknowledged: false, signature_key: null, ...(d.representative || {}) });
    setInternalNotes(d.internal_notes || '');
    setMgmtAssessment(d.management_assessment || '');
    setHasMgmt(!!d.has_management_assessment);
    const o = {}; (d.sections || []).forEach((s) => { o[s.key] = true; }); setOpen(o);
  };

  const start = async (lid = locId) => {
    if (!lid) { toast.error('Select a branch'); return; }
    await loadConfig(lid);
    const r = await axios.post(`${API}/operations/synovus/inspections`, { service_location_id: lid, inspection_date: date, is_demo: demo }, auth(token));
    hydrate(r.data);
  };

  const loadExisting = async (iid) => {
    const r = await axios.get(`${API}/operations/synovus/inspections/${iid}`, auth(token));
    const d = r.data;
    setDate(String(d.inspection_date || '').slice(0, 10) || date);
    await loadConfig(d.service_location_id, d.additional_services);
    hydrate(d);
  };

  useEffect(() => {
    if (presetInspectionId) loadExisting(presetInspectionId);
    else if (presetLocationId && !doc) start(presetLocationId);
  }, [presetInspectionId, presetLocationId]); // eslint-disable-line

  const patchSection = (key, patch) => setSections((prev) => prev.map((s) => s.key === key ? { ...s, ...patch } : s));

  const saveDraft = async (silent) => {
    if (!doc || savingRef.current) return null;
    savingRef.current = true; setSaving(true);
    try {
      const payload = { sections, representative: rep, additional_services: addl, internal_notes: internalNotes };
      if (canConfidential && mgmtAssessment.trim()) payload.management_assessment = mgmtAssessment;
      const r = await axios.put(`${API}/operations/synovus/inspections/${doc.id}`, payload, auth(token));
      setSections(r.data.sections);
      setRep((p) => ({ ...p, ...(r.data.representative || {}) }));
      setHasMgmt(!!r.data.has_management_assessment);
      setSavedAt(etTime());
      if (!silent) toast.success(`Draft saved · ${etTime()}`);
      return r.data;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Save failed');
      return null;
    } finally { savingRef.current = false; setSaving(false); }
  };

  const clearSignature = async () => {
    if (!window.confirm('Clear the confirmed signature? The previous signature is archived for internal records. This is an explicit action.')) return;
    if (doc && rep.signature_key) { try { await axios.post(`${API}/operations/synovus/inspections/${doc.id}/signature/clear`, {}, auth(token)); } catch (e) { toast.error('Could not clear'); return; } }
    await refreshRep();
  };

  const removePhoto = async (pid) => {
    try {
      await axios.delete(`${API}/operations/synovus/inspections/${doc.id}/photo/${pid}`, auth(token));
      const fresh = await axios.get(`${API}/operations/synovus/inspections/${doc.id}`, auth(token));
      setDoc(fresh.data); setSections(fresh.data.sections);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove photo'); }
  };

  const openClientReview = async () => {
    if (!phoneValid(rep.phone) || !emailValid(rep.email)) { toast.error('Fix the representative phone/email format first'); return; }
    const saved = await saveDraft(true);
    if (saved === null) { toast.error('Could not save the draft — try again before handing over the tablet'); return; }
    setShowClientReview(true);
  };

  const refreshRep = async () => {
    const fresh = await axios.get(`${API}/operations/synovus/inspections/${doc.id}`, auth(token));
    setRep((p) => ({ ...p, ...(fresh.data.representative || {}) }));
  };

  const uploadPhoto = async (form) => {
    await saveDraft(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    await axios.post(`${API}/operations/synovus/inspections/${doc.id}/photo`, fd, { headers: { ...auth(token).headers } });
    const fresh = await axios.get(`${API}/operations/synovus/inspections/${doc.id}`, auth(token));
    setDoc(fresh.data); setSections(fresh.data.sections);
  };

  const newDeficiency = () => ({ id: Math.random().toString(36).slice(2, 10), description: '', severity: 'Medium', worker_note: '', poa: { steps: '', owner_id: null, target_date: '', pending: false } });
  const addDeficiency = (key) => patchSection(key, { deficiencies: [...(sections.find((s) => s.key === key).deficiencies || []), newDeficiency()] });
  const patchDef = (key, did, patch) => patchSection(key, { deficiencies: sections.find((s) => s.key === key).deficiencies.map((d) => d.id === did ? { ...d, ...patch } : d) });

  // Rating change: a 1/2 immediately reveals (creates) a required deficiency entry.
  // Moving 1/2 → 3-5 never silently deletes what was entered — the inspector removes
  // it deliberately (a note prompts them).
  const setRating = (key, v) => {
    const sec = sections.find((s) => s.key === key) || {};
    const patch = { status: 'rated', rating: v, reason: null };
    if ((v === 1 || v === 2) && (sec.deficiencies || []).length === 0) {
      patch.deficiencies = [newDeficiency()];
    }
    patchSection(key, patch);
  };

  // Preview/Download work on the current draft WITHOUT submission validation.
  // They save the latest edits first, then open a read-only report — never submit,
  // never send a message, never create a corrective action, never change status.
  const previewReport = async () => {
    if (!doc) return;
    const saved = await saveDraft(true);
    if (saved === null) { toast.error('Could not save the latest edits — try again'); return; }
    setShowPreview(true);
  };
  const downloadDraftPdf = async () => {
    if (!doc) return;
    const saved = await saveDraft(true);
    if (saved === null) { toast.error('Could not save the latest edits — try again'); return; }
    window.open(`${API}/operations/synovus/inspections/${doc.id}/report/qc.pdf?lang=${formLang}&auth=${token}`, '_blank');
  };

  const submit = async () => {
    if (!phoneValid(rep.phone) || !emailValid(rep.email)) { toast.error('Fix the representative phone/email format before submitting'); return; }
    setBusy(true); setErrs([]);
    try {
      await saveDraft(true);
      const r = await axios.post(`${API}/operations/synovus/inspections/${doc.id}/submit`, {}, auth(token));
      setResult(r.data); toast.success('Inspection submitted');
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.issues) { setErrs(d.issues); toast.error('Please resolve the highlighted items'); }
      else toast.error(d?.message || d || 'Submit failed');
    } finally { setBusy(false); }
  };

  const confirmedSig = !!(rep.signature_key && rep.acknowledged);
  const pendingSig = !!rep.pending_signature_key && !rep.acknowledged;

  // ---- Start screen ----
  if (!doc) {
    if (presetLocationId || presetInspectionId) {
      return <div className="mx-auto max-w-xl py-10 text-center text-sm text-slate-500" data-testid="syn-loading">Loading Synovus branch inspection form…</div>;
    }
    return (
      <div className="mx-auto max-w-xl" data-testid="syn-start">
        <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2A47]"><ArrowLeft size={15} /> Back</button>
        <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Synovus Bank</div>
        <h1 className="mt-1 text-[26px] font-extrabold text-[#0E2A47]">Branch Quality-Control Inspection</h1>
        <p className="mt-1 text-sm text-slate-500">Tablet-friendly, section-based QC. Choose the branch (governed by Admin Hub Service Locations).</p>
        <div className="mt-5 space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
          <div><span className={lbl}>Branch</span>
            <select value={locId} onChange={(e) => setLocId(e.target.value)} className={inp} data-testid="syn-branch">
              <option value="">Select a Synovus branch…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.location_name} {b.location_code ? `(${b.location_code})` : ''}</option>)}
            </select>
          </div>
          <div><span className={lbl}>Inspection date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} data-testid="syn-date" /></div>
          <label className="flex items-center gap-2 text-sm font-medium text-fuchsia-600"><input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} className="h-4 w-4 accent-fuchsia-500" data-testid="syn-demo" /> Demo record (excluded from real counts)</label>
          <button onClick={() => start()} data-testid="syn-start-btn" className="w-full rounded-lg bg-[#0E2A47] py-3 text-sm font-bold text-white hover:bg-[#0E2A47]/90">Start Inspection</button>
        </div>
      </div>
    );
  }

  // ---- Post-submit summary ----
  if (result) {
    return (
      <div className="mx-auto max-w-xl" data-testid="syn-result">
        <div className="rounded-2xl border border-[#8FC93A]/40 bg-[#8FC93A]/10 p-5 text-center">
          <CheckCircle2 className="mx-auto mb-2 text-[#4d7c0f]" size={34} />
          <h1 className="text-xl font-extrabold text-[#0E2A47]">{result.inspection_number} submitted</h1>
          <p className="mt-1 text-sm text-slate-600">{result.corrective_actions_created.length} Plan(s) of Action (POA) · {result.employee_notices.length} employee notice(s) drafted{result.translations_drafted > 0 ? ` · ${result.translations_drafted} Spanish draft(s)` : ''}</p>
        </div>
        <button onClick={() => setShowReview(true)} data-testid="syn-open-review" className="mt-4 w-full rounded-lg bg-[#0E2A47] py-3 text-sm font-bold text-white">Open Report &amp; Delivery Review (4 audiences)</button>
        <p className="mt-3 text-xs text-slate-400">Client delivery &amp; employee notices are disabled in Preview. The Additional Services report is separate and never auto-attached or sent.</p>
        <button onClick={onBack} className="mt-4 text-sm text-slate-500 hover:text-[#0E2A47]">← Back to Inspections</button>
        {showReview && <SynovusReview inspectionId={doc.id} inspectionNumber={result.inspection_number} onClose={() => setShowReview(false)} />}
      </div>
    );
  }

  // ---- Inspection form ----
  return (
    <AutoTranslate lang={formLang}>
    <div className="mx-auto max-w-2xl" data-testid="syn-form">
      {!embedded && <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2A47]"><ArrowLeft size={15} /> Back</button>}
      <div className="flex items-center gap-2">
        <h1 className="text-[22px] font-extrabold text-[#0E2A47]">{doc.inspection_number}</h1>
        {doc.is_demo && <span className="rounded bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold text-fuchsia-700">DEMO</span>}
        <div className="ml-auto inline-flex overflow-hidden rounded-lg border border-slate-300" data-no-translate="">
          <button onClick={() => setFormLang('en')} data-testid="syn-form-lang-en" className={`px-2.5 py-1 text-xs font-semibold ${formLang === 'en' ? 'bg-[#0E2A47] text-white' : 'bg-white text-[#0E2A47]'}`}>English</button>
          <button onClick={() => setFormLang('es')} data-testid="syn-form-lang-es" className={`px-2.5 py-1 text-xs font-semibold ${formLang === 'es' ? 'bg-[#0E2A47] text-white' : 'bg-white text-[#0E2A47]'}`}>Español</button>
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-500">{doc.customer_name} — {doc.location_name} · {date}</p>

      {/* Top action bar — mirrors the bottom bar */}
      <div className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button onClick={() => saveDraft(false)} disabled={saving} data-testid="syn-action-save-top" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-100 disabled:opacity-50"><Save size={15} /> {saving ? 'Saving…' : 'Save Draft'}</button>
          <button onClick={previewReport} data-testid="syn-action-preview-top" title="Open a read-only preview of the reports with current answers — does not submit or send" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] bg-white px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5"><Eye size={15} /> Preview PDF</button>
          <button onClick={downloadDraftPdf} data-testid="syn-action-download-top" title="Download the draft QC PDF with current answers — does not submit or send" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] bg-white px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5"><Download size={15} /> Download PDF</button>
          <button onClick={submit} disabled={busy} data-testid="syn-submit-top" className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#8FC93A] px-3 py-2.5 text-sm font-bold text-[#0E2A47] hover:bg-[#8FC93A]/90 disabled:opacity-50">{busy ? 'Submitting…' : 'Submit Inspection'}</button>
        </div>
        <button onClick={() => setShowTrans(true)} data-testid="syn-trans-top" title="Opens an AI-assisted Spanish translation review of your free-text notes — it is NOT the report preview" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-[#0E2A47] hover:bg-slate-100"><Languages size={14} /> Review in Spanish</button>
        {savedAt && <span className="ml-2 text-xs text-[#4d7c0f]">Saved · {savedAt}</span>}
      </div>

      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="syn-scale">
        <b>Rating scale:</b> {SCALE.map(([v, l]) => `${v} ${l}`).join(' · ')}. Nothing is pre-selected. <b>Not Inspected</b> / <b>Not Applicable</b> require a reason and are not favorable ratings.
      </div>

      {errs.length > 0 && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="syn-errors">
          <div className="font-semibold">Resolve before submitting:</div>
          <ul className="ml-4 list-disc">{errs.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {cleaners && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="syn-assigned-cleaners">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Users size={13} /> Assigned Cleaner (from Admin Hub) — internal</div>
          {cleaners.needs_assignment ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="syn-cleaner-needs-assignment">Needs Assignment — no active cleaner is linked to this location in the Admin Hub. Operations must assign one; a person is never auto-invented.</div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {cleaners.cleaners.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`syn-cleaner-${c.id}`}>
                  {c.photo_url ? (
                    <img src={`${API}${c.photo_url}?auth=${token}`} alt={c.name} className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Users size={18} /></div>
                  )}
                  <div className="text-xs">
                    <div className="font-semibold text-[#0E2A47]">{c.name || 'unavailable'}</div>
                    <div className="text-slate-500">Emp #{c.personnel_number || 'unavailable'}</div>
                    <div className="text-slate-500">{c.phone || 'unavailable'} · {c.email || 'unavailable'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-1.5 text-[10px] text-slate-400">Shown only on Operations, Assigned-Cleaner and Confidential reports — never on the client copy.</p>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {sections.map((s) => {
          const cfg = (config?.sections || []).find((x) => x.key === s.key) || {};
          const isCF = s.kind === 'client_feedback' || s.key === 'overall_feedback';
          const headerExtra = isCF
            ? (s.perception_rating ? ` · perception ${s.perception_rating}` : (s.feedback_provided === false ? ' · no feedback' : ''))
            : (s.status === 'rated' && s.rating ? ` · ${s.rating}` : s.status ? ` · ${s.status.replace('_', ' ')}` : '');
          return (
            <div key={s.key} className="rounded-xl border border-slate-200 bg-white" data-testid={`syn-section-${s.key}`}>
              <button type="button" onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))} className="flex w-full items-center justify-between rounded-t-xl bg-[#0E2A47] px-4 py-3 text-left text-white">
                <span className="font-semibold">{isCF ? (cfg.title || 'On-Site Client Feedback') : s.title}{headerExtra}</span>
                {open[s.key] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              {open[s.key] && isCF && (
                <div className="space-y-3 p-4" data-testid="syn-client-feedback-body">
                  <p className="text-xs text-slate-500">{cfg.guidance}</p>
                  <div className="rounded-lg bg-sky-50 px-3 py-2 text-[11px] text-sky-800">Recorded separately from the five inspected areas. Optional and unweighted — it never changes an area rating, conceals a deficiency, or overrides the inspector's findings. Never prefill or infer the client's opinion.</div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => patchSection(s.key, { feedback_provided: true })} data-testid="syn-cf-provided"
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.feedback_provided === true ? 'border-[#8FC93A] bg-[#8FC93A]/20 text-[#4d7c0f]' : 'border-slate-300 text-slate-600'}`}>Feedback provided</button>
                    <button type="button" onClick={() => patchSection(s.key, { feedback_provided: false, perception_rating: null, rep_name: null, rep_role: null, comments: null })} data-testid="syn-cf-none"
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.feedback_provided === false ? 'border-slate-500 bg-slate-100' : 'border-slate-300 text-slate-500'}`}>No feedback provided</button>
                  </div>
                  {s.feedback_provided === true && (
                    <>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div><span className={lbl}>Representative name (if available)</span><input value={s.rep_name || ''} onChange={(e) => patchSection(s.key, { rep_name: e.target.value })} className={inp} data-testid="syn-cf-name" /></div>
                        <div><span className={lbl}>Role / title (if available)</span><input value={s.rep_role || ''} onChange={(e) => patchSection(s.key, { rep_role: e.target.value })} className={inp} data-testid="syn-cf-role" /></div>
                      </div>
                      <div>
                        <span className={lbl}>Optional client perception (1–5) — nothing pre-selected</span>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {SCALE.map(([v, l]) => (
                            <button key={v} type="button" onClick={() => patchSection(s.key, { perception_rating: v })} data-testid={`syn-cf-rate-${v}`}
                              className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.perception_rating === v ? 'border-sky-500 bg-sky-100 text-sky-800' : 'border-slate-300 text-slate-600'}`}>{v} {l}</button>
                          ))}
                          {s.perception_rating && <button type="button" onClick={() => patchSection(s.key, { perception_rating: null })} data-testid="syn-cf-rate-clear" className="rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50">Clear</button>}
                        </div>
                      </div>
                      <div>
                        <span className={lbl}>Representative comments (optional)</span>
                        <textarea value={s.comments || ''} onChange={(e) => patchSection(s.key, { comments: e.target.value })} rows={2} className={inp} placeholder="Record the feedback as given — do not infer or embellish" data-testid="syn-cf-comments" />
                      </div>
                    </>
                  )}
                  {s.feedback_provided === false && <p className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-500">Will report: “No feedback provided (representative unavailable or declined).”</p>}
                </div>
              )}
              {open[s.key] && !isCF && (
                <div className="space-y-3 p-4">
                  <p className="text-xs text-slate-500">{cfg.guidance}</p>
                  {cfg.weekly_note && <p className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-700">{cfg.weekly_note}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {SCALE.map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setRating(s.key, v)}
                        data-testid={`syn-rate-${s.key}-${v}`}
                        className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.status === 'rated' && s.rating === v ? 'border-[#8FC93A] bg-[#8FC93A]/20 text-[#4d7c0f]' : 'border-slate-300 text-slate-600'}`}>{v} {l}</button>
                    ))}
                    <button type="button" onClick={() => patchSection(s.key, { status: 'not_inspected', rating: null })} data-testid={`syn-ni-${s.key}`} className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.status === 'not_inspected' ? 'border-slate-500 bg-slate-100' : 'border-slate-300 text-slate-500'}`}>Not Inspected</button>
                    <button type="button" onClick={() => patchSection(s.key, { status: 'not_applicable', rating: null })} data-testid={`syn-na-${s.key}`} className={`rounded-lg border px-3 py-2 text-xs font-semibold ${s.status === 'not_applicable' ? 'border-slate-500 bg-slate-100' : 'border-slate-300 text-slate-500'}`}>Not Applicable</button>
                  </div>
                  {(s.status === 'not_inspected' || s.status === 'not_applicable') && (
                    <input value={s.reason || ''} onChange={(e) => patchSection(s.key, { reason: e.target.value })} placeholder="Reason (required)" className={inp} data-testid={`syn-reason-${s.key}`} />
                  )}
                  {s.status === 'rated' && (s.rating === 1 || s.rating === 2) && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-700" data-testid={`syn-lowrate-prompt-${s.key}`}>
                      <div className="flex items-center gap-1.5 font-bold"><AlertTriangle size={13} /> A {s.rating} ({s.rating === 1 ? 'Poor' : 'Needs Improvement'}) rating requires a documented deficiency</div>
                      <p className="mt-1">Record what is wrong below and provide, for each deficiency: a <b>description</b>, <b>corrective-action steps</b> and a <b>before photo</b>. Owner and target date may stay pending assignment. Add a separate entry for each distinct issue in this area. You cannot submit until this is complete.</p>
                    </div>
                  )}
                  {s.status === 'rated' && s.rating >= 3 && (s.deficiencies || []).length > 0 && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-700" data-testid={`syn-rate-changed-note-${s.key}`}>
                      This area is now rated {s.rating}, but it still has {(s.deficiencies || []).length} deficiency entr{(s.deficiencies || []).length === 1 ? 'y' : 'ies'} recorded. Nothing was deleted — review below and remove any that no longer apply.
                    </div>
                  )}

                  {/* Audience-separated notes */}
                  <div>
                    <span className={lbl}>Client-facing note — included in client report</span>
                    <textarea value={s.client_note || s.note || ''} onChange={(e) => patchSection(s.key, { client_note: e.target.value })} rows={2} className={inp} data-testid={`syn-clientnote-${s.key}`} />
                  </div>
                  <div className="rounded-lg border border-[#8FC93A]/40 bg-[#8FC93A]/5 p-2">
                    <span className={lbl + ' text-[#4d7c0f]'}><Lock size={11} className="mr-1 inline" />Internal operations note — not shown to client</span>
                    <textarea value={s.ops_note || ''} onChange={(e) => patchSection(s.key, { ops_note: e.target.value })} rows={2} className={inp + ' bg-white'} data-testid={`syn-opsnote-${s.key}`} />
                  </div>
                  {canConfidential && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-2">
                      <span className={lbl + ' text-red-700'}><ShieldAlert size={11} className="mr-1 inline" />Confidential management note — Sergio only</span>
                      {s.has_mgmt_note && !isSergio && <p className="mb-1 text-[11px] text-red-600">A confidential note is on file (hidden — only Sergio can read it). Typing here replaces it.</p>}
                      <textarea value={s.mgmt_note || ''} onChange={(e) => patchSection(s.key, { mgmt_note: e.target.value })} rows={2} className={inp + ' bg-white'} placeholder={isSergio ? '' : 'Visible only to Sergio Perez'} data-testid={`syn-mgmtnote-${s.key}`} />
                    </div>
                  )}

                  <div className="space-y-2">
                    <div>
                      {(() => {
                        const n = (s.photos || []).filter((p) => p.kind === 'section').length;
                        const required = s.status === 'rated' || (s.deficiencies || []).some((d) => (d.description || '').trim());
                        return (
                          <div className="mb-1 flex items-center gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Section photos — shown to client</span>
                            {required && (
                              <span data-testid={`syn-photocount-${s.key}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${n >= 3 ? 'bg-[#8FC93A]/20 text-[#4d7c0f]' : 'bg-red-100 text-red-700'}`}>{n}/3 {n >= 3 ? '✓' : 'min. required'}</span>
                            )}
                          </div>
                        );
                      })()}
                      <PhotoControls testid={`syn-photo-${s.key}`} onPick={(f) => uploadPhoto({ file: f, section_key: s.key, kind: 'section', client_visible: 'true', caption: s.title })} />
                      <PhotoThumbs photos={(s.photos || []).filter((p) => p.kind === 'section')} iid={doc.id} token={token} onRemove={removePhoto} />
                    </div>
                    <div className="rounded-lg border border-[#8FC93A]/40 bg-[#8FC93A]/5 p-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#4d7c0f]"><Lock size={11} className="mr-1 inline" />Internal photos — not shown to client</div>
                      <PhotoControls testid={`syn-internal-${s.key}`} onPick={(f) => uploadPhoto({ file: f, section_key: s.key, kind: 'internal', client_visible: 'false', caption: 'Internal' })} />
                      <PhotoThumbs photos={(s.photos || []).filter((p) => p.kind === 'internal')} iid={doc.id} token={token} onRemove={removePhoto} />
                    </div>
                  </div>

                  <div className="rounded-lg border border-dashed border-slate-300 p-3">
                    <div className="mb-1 flex items-center justify-between"><span className="text-xs font-semibold text-slate-600">Deficiencies</span>
                      <button type="button" onClick={() => addDeficiency(s.key)} data-testid={`syn-adddef-${s.key}`} className="inline-flex items-center gap-1 rounded bg-[#0E2A47] px-2 py-1 text-[11px] font-semibold text-white"><Plus size={12} /> Add</button></div>
                    {(s.deficiencies || []).map((d) => (
                      <div key={d.id} className="mt-2 space-y-2 rounded-lg bg-slate-50 p-2" data-testid={`syn-def-${d.id}`}>
                        <textarea value={d.description} onChange={(e) => patchDef(s.key, d.id, { description: e.target.value })} placeholder="Deficiency description" rows={2} className={inp} data-testid={`syn-def-desc-${d.id}`} />
                        <div className="flex items-center gap-2">
                          <select value={d.severity} onChange={(e) => patchDef(s.key, d.id, { severity: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs">{SEV.map((x) => <option key={x}>{x}</option>)}</select>
                        </div>
                        <div>
                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Before photo (required evidence)</div>
                          {d.before_photo?.id ? (
                            <PhotoThumbs photos={[d.before_photo]} iid={doc.id} token={token} onRemove={removePhoto} />
                          ) : (
                            <PhotoControls testid={`syn-def-before-${d.id}`} onPick={(f) => uploadPhoto({ file: f, deficiency_id: d.id, kind: 'before', client_visible: 'true', caption: 'Before' })} />
                          )}
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <span className={lbl}>Plan of Action (correction steps)</span>
                          <textarea value={d.poa?.steps || ''} onChange={(e) => patchDef(s.key, d.id, { poa: { ...d.poa, steps: e.target.value } })} placeholder="Corrective steps" rows={2} className={inp} data-testid={`syn-poa-steps-${d.id}`} />
                          <span className={lbl + ' mt-2'}>Cleaner note (worker copy only, optional)</span>
                          <input value={d.worker_note || ''} onChange={(e) => patchDef(s.key, d.id, { worker_note: e.target.value })} placeholder="Extra instruction for the assigned cleaner" className={inp} data-testid={`syn-worker-note-${d.id}`} />
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <select value={d.poa?.owner_id || ''} onChange={(e) => patchDef(s.key, d.id, { poa: { ...d.poa, owner_id: e.target.value || null } })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid={`syn-poa-owner-${d.id}`}>
                              <option value="">Owner: Needs assignment</option>
                              {personnel.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <input type="date" value={d.poa?.target_date || ''} onChange={(e) => patchDef(s.key, d.id, { poa: { ...d.poa, target_date: e.target.value } })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid={`syn-poa-date-${d.id}`} />
                            <label className="flex items-center gap-1 text-[11px] text-slate-500"><input type="checkbox" checked={d.poa?.pending || false} onChange={(e) => patchDef(s.key, d.id, { poa: { ...d.poa, pending: e.target.checked } })} className="h-3.5 w-3.5 accent-amber-500" data-testid={`syn-poa-pending-${d.id}`} /> Pending assignment/date</label>
                          </div>
                        </div>
                        <button type="button" onClick={() => patchSection(s.key, { deficiencies: s.deficiencies.filter((x) => x.id !== d.id) })} className="text-[11px] text-red-500 hover:underline">Remove deficiency</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Representative */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="syn-rep">
        <div className="mb-2 font-semibold text-[#0E2A47]">Branch Representative</div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setRep((p) => ({ ...p, present: true, declined: false }))} data-testid="syn-rep-present" className={`rounded-lg border px-3 py-2 text-xs font-semibold ${rep.present === true ? 'border-[#8FC93A] bg-[#8FC93A]/20 text-[#4d7c0f]' : 'border-slate-300 text-slate-600'}`}>Present</button>
          <button type="button" onClick={() => setRep((p) => ({ ...p, present: false }))} data-testid="syn-rep-absent" className={`rounded-lg border px-3 py-2 text-xs font-semibold ${rep.present === false ? 'border-slate-500 bg-slate-100' : 'border-slate-300 text-slate-600'}`}>Not available</button>
        </div>
        {rep.present && (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-slate-400">Entered here only — this never overwrites an Admin Hub contact. The representative reviews and signs on a separate clean screen.</p>
            <div className="grid grid-cols-2 gap-2">
              <input value={rep.name || ''} onChange={(e) => setRep((p) => ({ ...p, name: e.target.value }))} placeholder="Name" className={inp} data-testid="syn-rep-name" />
              <input value={rep.title || ''} onChange={(e) => setRep((p) => ({ ...p, title: e.target.value }))} placeholder="Title" className={inp} data-testid="syn-rep-title" />
              <div>
                <input value={rep.phone || ''} onChange={(e) => setRep((p) => ({ ...p, phone: e.target.value }))}
                  onBlur={(e) => setRep((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
                  inputMode="tel" placeholder="Phone — (###) ###-####" data-testid="syn-rep-phone"
                  className={`${inp} ${phoneValid(rep.phone) ? '' : 'border-red-400'}`} />
                {!phoneValid(rep.phone) && <p className="mt-0.5 text-[10px] text-red-600" data-testid="syn-rep-phone-error">Enter a 10-digit US phone number (or leave blank).</p>}
              </div>
              <div>
                <input value={rep.email || ''} onChange={(e) => setRep((p) => ({ ...p, email: e.target.value }))}
                  inputMode="email" placeholder="Email" data-testid="syn-rep-email"
                  className={`${inp} ${emailValid(rep.email) ? '' : 'border-red-400'}`} />
                {!emailValid(rep.email) && <p className="mt-0.5 text-[10px] text-red-600" data-testid="syn-rep-email-error">Enter a valid email address (or leave blank).</p>}
              </div>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <p className="text-[11px] text-sky-800">Hand the tablet over only on the clean review screen. It shows the client-facing findings and photos only — never internal operations, confidential, or additional-services content.</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" onClick={openClientReview} data-testid="syn-open-client-review"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0E2A47]/90"><Users size={15} /> Open Client / Branch Review &amp; Sign</button>
                {confirmedSig && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#4d7c0f]" data-testid="syn-sig-status"><CheckCircle2 size={13} /> Signature confirmed and saved</span>}
                {pendingSig && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="syn-sig-pending-status"><CheckCircle2 size={13} /> Signature saved — confirmation required</span>}
                {rep.declined && <span className="text-xs font-semibold text-slate-500" data-testid="syn-declined-status">Representative declined to sign</span>}
                {confirmedSig && <button type="button" onClick={clearSignature} data-testid="syn-sig-clear" className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs">Clear signature</button>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Additional Services */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="syn-additional">
        <div className="font-semibold text-[#0E2A47]">Additional Services Observed</div>
        <p className="mb-2 text-[11px] text-slate-400">Opportunities for review only — never scored, never a corrective action, never priced or scheduled. Subject to separate proposal.</p>
        {addl.map((a, i) => (
          <div key={a.key} className="border-t border-slate-100 py-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={a.observed} onChange={(e) => setAddl((prev) => prev.map((x, j) => j === i ? { ...x, observed: e.target.checked } : x))} className="h-4 w-4 accent-[#8FC93A]" data-testid={`syn-addl-${a.key}`} /> {a.label}</label>
            {a.observed && (
              <div className="mt-2 space-y-2 pl-6">
                <input value={a.location} onChange={(e) => setAddl((prev) => prev.map((x, j) => j === i ? { ...x, location: e.target.value } : x))} placeholder="Location on site" className={inp} />
                <input value={a.note} onChange={(e) => setAddl((prev) => prev.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} placeholder="Observed condition / note" className={inp} />
                <PhotoControls testid={`syn-addl-photo-${a.key}`} onPick={(f) => uploadPhoto({ file: f, section_key: a.key, kind: 'section', client_visible: 'true', caption: a.label })} />
                {(() => {
                  const pics = (doc?.photos || []).filter((p) => p.section_key === a.key && p.kind === 'section' && p.client_visible);
                  return (
                    <div data-testid={`syn-addl-photos-${a.key}`}>
                      <div className="mb-1 text-[11px] font-semibold text-[#4d7c0f]">{pics.length} photo{pics.length === 1 ? '' : 's'} attached to “{a.label}”</div>
                      <div className="flex flex-wrap gap-2">
                        {pics.map((p) => (
                          <div key={p.id} className="relative w-20" data-testid={`syn-addl-thumb-${p.id}`}>
                            <img src={`${API}/operations/synovus/inspections/${doc.id}/photo/${p.id}/raw?auth=${token}`} alt={p.caption || 'photo'}
                              onClick={() => window.open(`${API}/operations/synovus/inspections/${doc.id}/photo/${p.id}/raw?auth=${token}`, '_blank')}
                              className="h-16 w-20 cursor-pointer rounded border border-slate-200 object-cover" />
                            <div className="truncate text-[9px] text-slate-500" title={p.caption}>{p.caption || p.id.slice(0, 6)}</div>
                            <button type="button" data-testid={`syn-addl-remove-${p.id}`} onClick={async () => { await axios.delete(`${API}/operations/synovus/inspections/${doc.id}/photo/${p.id}`, auth(token)); const fresh = await axios.get(`${API}/operations/synovus/inspections/${doc.id}`, auth(token)); setDoc(fresh.data); setSections(fresh.data.sections); }}
                              className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white" title="Remove"><Trash2 size={11} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Internal operations note (top-level) */}
      <div className="mt-4 rounded-xl border border-[#8FC93A]/40 bg-[#8FC93A]/5 p-4">
        <span className={lbl + ' text-[#4d7c0f]'}><Lock size={11} className="mr-1 inline" />Internal operations note — not shown to client</span>
        <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2} className={inp + ' bg-white'} data-testid="syn-internal-notes" />
      </div>

      {/* Confidential management assessment — Sergio only */}
      {canConfidential && (
        <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-4" data-testid="syn-mgmt-assessment-box">
          <span className={lbl + ' text-red-700'}><ShieldAlert size={11} className="mr-1 inline" />Confidential management assessment — Sergio Perez only</span>
          {hasMgmt && !isSergio && <p className="mb-1 text-[11px] text-red-600">A confidential assessment is on file (hidden — only Sergio can read it). Typing here replaces it.</p>}
          <textarea value={mgmtAssessment} onChange={(e) => setMgmtAssessment(e.target.value)} rows={3} className={inp + ' bg-white'} placeholder={isSergio ? 'Repeated failures, staffing recommendations, sensitive escalations…' : 'Visible only to Sergio Perez'} data-testid="syn-mgmt-assessment" />
        </div>
      )}

      {/* Bottom actions — the four clearly-labeled editor actions + Spanish review */}
      <div className="mt-5 space-y-2" data-testid="syn-action-bar">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button onClick={() => saveDraft(false)} disabled={saving} data-testid="syn-action-save" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 disabled:opacity-50"><Save size={15} /> {saving ? 'Saving…' : 'Save Draft'}</button>
          <button onClick={previewReport} data-testid="syn-action-preview" title="Open a read-only preview of the reports with current answers — does not submit or send" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5"><FileText size={15} /> Preview Report</button>
          <button onClick={downloadDraftPdf} data-testid="syn-action-download" title="Open/download the draft QC PDF with current answers — does not submit or send" className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] px-3 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5"><FileText size={15} /> Download Draft PDF</button>
          <button onClick={submit} disabled={busy} data-testid="syn-submit" className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#8FC93A] px-3 py-2.5 text-sm font-bold text-[#0E2A47] hover:bg-[#8FC93A]/90 disabled:opacity-50">{busy ? 'Submitting…' : 'Submit Inspection'}</button>
        </div>
        <p className="text-[11px] text-slate-400">Preview and Download work before all questions are answered — unanswered items show as “Not answered” and the report is marked <b>DRAFT — INCOMPLETE</b>. Only <b>Submit Inspection</b> requires every answer and creates Plans of Action. <b>Review in Spanish</b> (top) is a separate Spanish-translation review of your notes, not the report preview.</p>
        {savedAt && <span className="text-xs text-[#4d7c0f]" data-testid="syn-action-saved-at">Saved · {savedAt}</span>}
      </div>

      {showPreview && (
        <SynovusReview
          inspectionId={doc.id}
          inspectionNumber={doc.inspection_number || doc.id}
          preview
          defaultLang={formLang}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showTrans && <TranslationPanel token={token} inspectionId={doc.id} onClose={() => setShowTrans(false)} />}
      {showClientReview && (
        <SynovusClientReview
          inspectionId={doc.id}
          onClose={() => { setShowClientReview(false); refreshRep(); }}
          onUpdated={refreshRep}
        />
      )}
    </div>
    </AutoTranslate>
  );
};

// English↔Spanish review — AI drafts, reviewer edits/approves; original English is
// preserved and never silently replaced.
const TranslationPanel = ({ token, inspectionId, onClose }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (draft) => {
    setBusy(true);
    try {
      if (draft) {
        const r = await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/translate?lang=es`, {}, auth(token));
        mesh(r.data);
      } else {
        const r = await axios.get(`${API}/operations/synovus/inspections/${inspectionId}`, auth(token));
        // derive fields client-side is not needed; ask translate with no drafting by reading stored translations
        const t = (r.data.translations || {}).es || {};
        const fr = await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/translate?lang=es`, {}, auth(token));
        mesh({ ...fr.data, translations: { ...t, ...fr.data.translations } });
      }
    } finally { setBusy(false); }
  };
  const mesh = (data) => {
    const map = data.translations || {};
    setRows((data.fields || []).map((f) => ({ ...f, es: map[f.path]?.text || '', reviewed: !!map[f.path]?.reviewed })));
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);

  const saveRow = async (row, reviewed) => {
    const r = await axios.put(`${API}/operations/synovus/inspections/${inspectionId}/translation`, { lang: 'es', path: row.path, text: row.es, reviewed }, auth(token));
    setRows((prev) => prev.map((x) => x.path === row.path ? { ...x, es: r.data.text, reviewed: r.data.reviewed } : x));
    toast.success(reviewed ? 'Approved' : 'Saved');
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" data-testid="syn-trans-panel">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-[#0E2A47]"><Languages size={18} className="mr-1 inline" />Spanish Translation Review</h2>
          <button onClick={onClose} className="text-sm text-slate-500">Close</button>
        </div>
        <p className="mb-3 text-xs text-slate-500">AI drafts Spanish for your free-text notes. The English original is preserved; a draft stays <b>unreviewed</b> until you approve it. Approve only accurate wording.</p>
        <button onClick={() => load(true)} disabled={busy} className="mb-3 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-[#0E2A47] disabled:opacity-50" data-testid="syn-trans-redraft">{busy ? 'Drafting…' : 'AI-draft / refresh Spanish'}</button>
        {!rows ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0 ? <p className="text-sm text-slate-400">No free-text to translate yet — add notes or deficiencies first.</p> : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.path} className="rounded-xl border border-slate-200 p-3" data-testid={`syn-trans-row-${row.path}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{row.label}</div>
                <div className="mt-1 rounded bg-slate-50 p-2 text-sm text-slate-600"><span className="text-[10px] font-bold text-slate-400">EN</span> {row.source}</div>
                <textarea value={row.es} onChange={(e) => setRows((prev) => prev.map((x) => x.path === row.path ? { ...x, es: e.target.value, reviewed: false } : x))} rows={2} className={inp + ' mt-2'} data-testid={`syn-trans-es-${row.path}`} />
                <div className="mt-1 flex items-center gap-2">
                  <button onClick={() => saveRow(row, false)} className="rounded border border-slate-300 px-2 py-1 text-xs">Save draft</button>
                  <button onClick={() => saveRow(row, true)} data-testid={`syn-trans-approve-${row.path}`} className="rounded bg-[#0E2A47] px-2 py-1 text-xs font-semibold text-white">Approve</button>
                  <span className={`text-[11px] font-semibold ${row.reviewed ? 'text-[#4d7c0f]' : 'text-amber-600'}`}>{row.reviewed ? '✓ Reviewed' : 'Unreviewed'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SynovusInspection;
